import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, TextField } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';
import { priceRegex } from '../../constants';
import { RootState } from '../../store';
import { createAutoBidRequestAPI, updateAutoBidRequestApi } from '../../apis';
import { AlertType, APIResponse, Auction, FreeKeyObject } from '../../utilities/types';
import { getApiErrorMessage, getBrowserNetworkInfo, getFormattedPrice, parseIntoNumber } from '../../utilities/helper';
import CustomAlert from '../elements/custom-alert.element';

// ✅ Validation Schema
const validationSchema = Yup.object().shape({
  increment_amount: Yup.string()
    .required('Incremen amount is required')
    .matches(
      priceRegex,
      'Increment amount must have up to 10 digits before decimal and up to 2 digits after decimal'
    ),
  max_amount: Yup.string()
    .required('Maximum amount is required')
    .matches(
      priceRegex,
      'Maximum amount must have up to 10 digits before decimal and up to 2 digits after decimal'
    ),
});

interface FormProps {
  auction: Auction;
  onSuccess?: () => void;
}

const AutoBidRequestWidget: React.FC<FormProps> = ({ auction, onSuccess }) => {
  const formikRef = useRef<FormikProps<{
    increment_amount: string;
    max_amount: string;
  }> | null>(null);

  const { user } = useSelector((state: RootState) => state.auth);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const autoBid = auction?.auto_bid_detail;

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const onsubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!formikRef.current) {
      return;
    }
    const { values } = formikRef.current;
    setIsSubmitting(true);
    try {
      const auto_bid_detail = auction?.auto_bid_detail || null;
      const payload = {
        bidder_id: user?.id,
        increment: values.increment_amount,
        max_amount: values.max_amount,
        network_info: await getBrowserNetworkInfo(),
      };
      let response: any = {};
      if (auto_bid_detail) {
        response = (await updateAutoBidRequestApi({
          ...payload,
          id: auto_bid_detail?.id,
        })) as APIResponse<FreeKeyObject>;
      } else {
        response = (await createAutoBidRequestAPI({
          ...payload,
          auction_id: auction?.id,
        })) as APIResponse<FreeKeyObject>;
      }
      if (response.status) {
        formikRef.current.resetForm();
        toggleAlertMessage(
          auto_bid_detail ? 'Auto bid updated successfully' : 'Auto bid created successfully',
          'success'
        );
        onSuccess?.();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const nextBidAmount =
    parseIntoNumber(auction?.current_price) + parseIntoNumber(auction?.increment_price);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        increment_amount: (autoBid ? autoBid.increment : auction.increment_price).toString(),
        max_amount: (autoBid ? autoBid.max_amount : '').toString(),
      }}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={(values) => {
        console.log('Form Submitted:', values);
      }}
      validate={(values) => {
        const errors: any = {};
        if (values.increment_amount.toString().trim() === '') {
          errors.increment_amount = 'Increment amount is required';
        } else if (
          parseIntoNumber(values.increment_amount) < parseIntoNumber(auction?.increment_price)
        ) {
          errors.increment_amount =
            'Increment amount must be equal or greater than bid increment amount';
        }
        if (values.max_amount.toString().trim() === '') {
          errors.max_amount = 'Increment amount is required';
        } else if (parseIntoNumber(values.max_amount) < nextBidAmount) {
          errors.increment_amount = 'Max amount must be equal or greater than next amount';
        }
        return errors;
      }}
    >
      {({ errors, touched, isValid, dirty, handleChange }) => (
        <Form>
          <Field
            as={TextField}
            name="increment_amount"
            label="Increment Amount *"
            type="number"
            placeholder={`Minimum ${getFormattedPrice(auction?.increment_price)}`}
            fullWidth
            margin="normal"
            onChange={handleChange}
            inputProps={{
              min: parseIntoNumber(auction?.increment_price),
              step: parseIntoNumber(auction?.increment_price),
            }}
            error={touched.increment_amount && Boolean(errors.increment_amount)}
            helperText={touched.increment_amount && errors.increment_amount}
          />

          <Field
            as={TextField}
            name="max_amount"
            label="Max. Amount *"
            type="number"
            placeholder="Enter your maximum bid amount"
            fullWidth
            margin="normal"
            onChange={handleChange}
            inputProps={{
              min: nextBidAmount,
              step: parseIntoNumber(auction?.increment_price),
            }}
            error={touched.max_amount && Boolean(errors.max_amount)}
            helperText={touched.max_amount && errors.max_amount}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
            onClick={(e) => onsubmit(e)}
            disabled={!isValid || !dirty || isSubmitting}
          >
            Submit
          </Button>

          {alertMessage.message !== '' && (
            <Box sx={{ my: 1 }}>
              <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default AutoBidRequestWidget;
